import { getHeadersWithToken } from "./api";
import { openAddToBoard } from "./addtoboard";
import { showToast } from "./toast.js";

export function initVisualizer() {
    if (document.querySelector('html.page-visualizerpage') === null) {
        console.debug('visualizer page not found, skipping visualizer init');
        return;
    }
    
    function getVisualizerType() {
        const container = document.querySelector('div.visualizer-container');
        return container?.dataset.visualizerType ?? null;
    }
    
    // table of visualizer configurations, keyed by VisualizerType
    //  the "buildModel" method should take the data from the visualizer and return a json object that can be sent to the 
    //  verification api. the "$type" property must match a type specified on "VisualizerModelBase".
    const visualizerConfigs = {
        "ActiveLines": {
            buildModel: (data) => {
                return {
                    "$type": "ActiveLinesVisualizerModel",
                    style: data?.style,
                    size: data?.size,
                    profile: data?.profile,
                    products: data?.products
                };
            }
        },
        "CustomCarpet": {
            buildModel: (data) => {
                return {
                    "$type": "CustomCarpetVisualizerModel",
                    projectUrl: data?.projectUrl,
                    renderUrl: data?.rugImageUrl
                }
            }
        },
        "CustomRug": {
            buildModel: (data) => {
                return {
                    "$type": "CustomRugVisualizerModel",
                    projectUrl: data?.projectUrl,
                    renderUrl: data?.rugImageUrl
                }
            }
        },
        "RoomScenes": {
            buildModel: (data) => {
                let submenu = null;
                
                if (data?.projectUrl) {
                    try {
                        const url = new URL(data.projectUrl);
                        submenu = url.searchParams.get('submenu');
                    } catch (e) {
                        console.warn('Failed to parse projectUrl:', e);
                    }
                }

                return {
                    "$type": "RoomSceneVisualizerModel",
                    selString: data?.selstring,
                    specifier: data?.specifier,
                    seriesIds: data?.seriesUsed,
                    submenu: submenu
                };
            }
        },
        "LayingPatterns": {
            buildModel: (data) => {
                return {
                    "$type": "LayingPatternsVisualizerModel",
                    activePatternId: data?.activePatternId,
                    productSelection: data?.productSelection
                }
            }
        }
    }
    
    function getConfig() {
        const type = getVisualizerType();
        
        if (!type) {
            throw new Error('no visualizer type found');
        }
        
        const config = visualizerConfigs[type];
        
        if (!config) {
            throw new Error(`no config found for visualizer type: ${type}`);
        }
        
        return config;
    }
    
    async function handleSaveRequest(data) {
        const config = getConfig();
        const visualizerInfo = config.buildModel(data);
        const visualizerInfoString = JSON.stringify(visualizerInfo);

        // Encrypt the visualizer info
        const response = await fetch('/api/visualizer/encrypt', {
            method: 'POST',
            headers: await getHeadersWithToken({ 'Content-Type': 'application/json'}),
            body: visualizerInfoString
        });

        if (!response.ok) {
            throw new Error('Failed to encrypt visualizer info');
        }

        // API returns a raw encrypted string, not a JSON object
        const encryptedString = await response.text();
        openAddToBoard(encryptedString);
        return { status: 'UI_OPENED' };
    }

    async function handleFetchProjects() {
        try {
            const api = await import('./api.js');
            const list = await api.projectBoardsList();
            return list?.items ?? [];
        } catch (err) {
            console.error('Error fetching project boards:', err);
            return [];
        }
    }

    window.addEventListener('message', (e) => {
        if (!e.data || !e.data.type) {
            return;
        }
        
        // todo: check origin

        switch (e.data.type) {
            case 'SAVE_REQUEST':
                handleSaveRequest(e.data.payload)
                    .then((result) => e.source?.postMessage({
                        type: 'SAVE_ACK',
                        receivedAt: Date.now(),
                        result
                    }, e.origin || '*'))
                    .catch(err => {
                        console.error('error handling save request', err);
                        showToast('Sorry, there was an error saving your visualizer data. Please try again later.', 'error');
                    });
                break;

            case 'FETCH_PROJECTS':
                handleFetchProjects()
                    .then(projects => e.source?.postMessage({
                        type: 'PROJECTS_DATA',
                        projects: projects,
                        queryParams: e.data.queryParams,
                        receivedAt: Date.now()
                    }, e.origin || '*'))
                    .catch(err => console.error('error fetching project data', err));
                break;

            default:
                console.warn(`unhandled message type: ${e.data.type}`, e.data);
                break;
        }
    });
}
  